// src/main.js
// Game bootstrap and initialization

import { GAME_CONFIG, SKILLS } from './game/constants.js';
import logger from './utils/logger.js';
import { IS_DEV } from './config/env.js';
import { gameState } from './game/gameState.js';
import { startGameLoop } from './game/tick.js';
import { formatTime } from './utils/format.js';
import { XP_TABLE } from './game/constants.js';

// Import router system
import { router } from './core/router.js';
import { setupRoutes } from './core/routeSetup.js';
import { skillRegistry, getSkillIcon } from './core/skillRegistry.js';

// Import UI components
import { openSettingsModal } from './ui/settingsModal.js';
import { openBankModal } from './ui/bankModal.js';
import { openShopModal } from './ui/shopModal.js';
import { openTomeModal, updateTomeButtonCount } from './ui/tomeModal.js';
import { showQuestModal, getQuestButtonText, hasQuestReadyToComplete, getActiveQuestTimerText } from './ui/questModal.js';
import { openGlossaryModal } from './ui/glossaryModal.js';
import { openItemDatabaseModal } from './ui/itemDatabaseModal.js';
import { showDiscoveryNotification } from './ui/discoveryNotification.js';
import { showOfflineModal } from './ui/offlineModal.js';
import { showWelcomeModal } from './ui/welcomeModal.js';
import { toastWarning } from './ui/toast.js';
import { initInventoryUI } from './ui/inventoryUI.js';
import { initEquipmentUI } from './ui/equipmentUI.js';
import { initPinnedQuestsUI } from './ui/pinnedQuestsUI.js';
import { initTooltips } from './ui/tooltip.js';
import { initTalentActivity } from './ui/talentActivity.js';

// Import systems
import { calculateOfflineProgress, applyOfflineProgress } from './systems/offlineProgress.js';
import { getTalentFlatBonus } from './systems/talentTree/talentTree.js';

// Pre-register skills that should be immediately available
// These will self-register when imported
// Order determines sidebar display - grouped by gatherer → processor relationships
import './skills/mining/index.js';       // Gather ores
import './skills/smithing/index.js';     // Process ores → bars → arrowheads
import './skills/woodcutting/index.js';  // Gather logs
import './skills/pyre/index.js';         // Burn logs
import './skills/woodworking/index.js';    // Logs → shafts/bows, arrowheads → arrows
import './skills/architecting/index.js'; // Logs → buildings
import './skills/fishing/index.js';      // Gather fish
import './skills/cooking/index.js';      // Cook fish
import './skills/farming/index.js';      // Grow crops/herbs
import './skills/alchemy/index.js';      // Herbs → potions
import './skills/pilfering/index.js';    // Get gold
import './skills/crafting/index.js';     // Mixed materials
import './skills/glyphbinding/index.js'; // Essence → glyphs (self-contained)
import './skills/gatherer/index.js';     // Pet food + rare materials
import './skills/husbandry/index.js';    // Pet management + contracts
import './skills/bounty/index.js';       // Contracts for gold/XP
import './skills/combat/index.js';       // Combat zones and mobs
import './skills/admin/index.js';

// Import skill singletons for starting tool reinitialization after game load
// All skills with tool purchase system need reinitialization
import { miningSkill } from './skills/mining/mining.js';
import { woodcuttingSkill } from './skills/woodcutting/woodcutting.js';
import { fishingSkill } from './skills/fishing/fishing.js';
import { pilferingSkill } from './skills/pilfering/pilfering.js';
import { farmingSkill } from './skills/farming/farming.js';
import { cookingSkill } from './skills/cooking/cooking.js';
import { pyreSkill } from './skills/pyre/pyre.js';
import { architectingSkill } from './skills/architecting/architecting.js';
import { craftingSkill } from './skills/crafting/crafting.js';
import { smithingSkill } from './skills/smithing/smithing.js';
import { woodworkingSkill } from './skills/woodworking/woodworking.js';
import { glyphbindingSkill } from './skills/glyphbinding/glyphbinding.js';
import { alchemySkill } from './skills/alchemy/alchemy.js';
import { gathererSkill } from './skills/gatherer/gatherer.js';

/**
 * Reinitialize starting tools for all skills with tool purchase system
 * Called after loading a saved game to ensure starting tools are always available
 * This fixes an issue where saves from before the tool system would lose starting tools
 */
function reinitializeStartingTools() {
    miningSkill.initializeStartingTool();
    woodcuttingSkill.initializeStartingTool();
    fishingSkill.initializeStartingTool();
    pilferingSkill.initializeStartingTool();
    farmingSkill.initializeStartingTool();
    cookingSkill.initializeStartingTool();
    pyreSkill.initializeStartingTool();
    architectingSkill.initializeStartingTool();
    craftingSkill.initializeStartingTool();
    smithingSkill.initializeStartingTool();
    woodworkingSkill.initializeStartingTool();
    glyphbindingSkill.initializeStartingTool();
    alchemySkill.initializeStartingTool();
    gathererSkill.initializeStartingTool();
    logger.debug('Reinitialized starting tools for all skills');
}

/**
 * Initialize the game
 */
function initGame() {
    logger.info('Initializing IdleRealms...');

    // Try to load saved game
    loadGame();

    // Initialize total level display
    gameState.recalculateTotalLevel();
    const totalLevelEl = document.getElementById('total-level');
    if (totalLevelEl) {
        totalLevelEl.textContent = gameState.player.stats.totalLevel;
    }

    // Setup UI
    initUI();

    // Apply inventory panel position setting
    if (gameState.settings.inventoryPanelLeft) {
        const gameMain = document.getElementById('game-main');
        if (gameMain) {
            gameMain.classList.add('inventory-left');
        }
    }

    // Setup routes before starting router
    setupRoutes();

    // Start game loop
    startGameLoop();

    // Start router - handles initial navigation
    router.init();

    // Setup save interval
    setInterval(() => {
        if (gameState.settings.autoSave) {
            saveGame();
        }
    }, GAME_CONFIG.SAVE_INTERVAL);

    // Save when tab is closed or hidden
    window.addEventListener('beforeunload', () => {
        saveGame();
    });

    // Also save when tab becomes hidden (mobile/switching tabs)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            saveGame();
        }
    });

    // Expose gameState to window for console access
    window.gameState = gameState;

    // Helper function to recalculate XP after manual level changes
    window.recalculateXP = function (skillId) {
        const skill = gameState.getSkill(skillId);

        // Import getLevelFromXP to calculate correct level from XP
        import('./systems/experience.js').then(module => {
            const correctLevel = module.getLevelFromXP(skill.xp);
            skill.level = correctLevel;

            const currentLevelXP = XP_TABLE[correctLevel - 1] || 0;
            const nextLevelXP = XP_TABLE[correctLevel] || XP_TABLE[XP_TABLE.length - 1];
            skill.currentXp = skill.xp - currentLevelXP;
            skill.nextLevelXp = nextLevelXP - currentLevelXP;

            logger.debug(`Recalculated ${skillId}:`);
            logger.debug(`   Level: ${skill.level}`);
            logger.debug(`   Total XP: ${skill.xp.toLocaleString()}`);
            logger.debug(`   Current Level Progress: ${skill.currentXp.toLocaleString()} / ${skill.nextLevelXp.toLocaleString()}`);
            logger.debug(`   XP to Next Level: ${(skill.nextLevelXp - skill.currentXp).toLocaleString()}`);

            // Router will handle re-rendering
            router.navigate(`#/${skillId}`, true);
        });
    };

    logger.info('Game initialized!');
    logger.debug('Use console commands like: gameState.getSkill("mining").level = 50');
    logger.debug('After setting level/XP manually, run: recalculateXP("mining")');
}

/**
 * Initialize UI elements
 */
function initUI() {
    // Initialize tooltip system
    initTooltips();

    // Populate skills sidebar from registry
    const skillsList = document.getElementById('skills-list');
    const visibleSkills = skillRegistry.getVisible(IS_DEV);

    for (const skillConfig of visibleSkills) {
        // Skip admin - it goes in the header
        if (skillConfig.id === 'admin') {
            continue;
        }

        const skill = gameState.getSkill(skillConfig.id);
        const skillDiv = document.createElement('div');
        skillDiv.className = 'skill-item';

        skillDiv.dataset.skill = skillConfig.id;

        // Combat skill gets HP display
        const hpDisplay = skillConfig.id === 'combat'
            ? '<span class="skill-hp"></span>'
            : '';

        skillDiv.innerHTML = `
      <div class="skill-icon">${getSkillIcon(skillConfig.id)}</div>
      <div class="skill-details">
        <span class="skill-name">${skillConfig.name}</span>
        <span class="skill-level">Level ${skill?.level || 1}/99${skill?.virtualLevel > 99 ? ` (${skill.virtualLevel})` : ''}</span>
      </div>
      ${hpDisplay}
    `;

        // Add click handler to navigate via router
        skillDiv.addEventListener('click', () => {
            // Check for saved tab preference
            const savedTab = localStorage.getItem(`idleRealms_tab_${skillConfig.id}`);
            if (savedTab && skillRegistry.hasTab(skillConfig.id, savedTab)) {
                router.navigate(`#/${skillConfig.id}/${savedTab}`);
            } else {
                router.navigate(`#/${skillConfig.id}`);
            }
        });

        skillsList.appendChild(skillDiv);
    }

    // Setup skills sidebar scroll indicator
    const skillsSidebar = document.getElementById('skills-sidebar');

    // Add sticky scroll indicator element
    const scrollIndicator = document.createElement('div');
    scrollIndicator.className = 'scroll-indicator';
    scrollIndicator.textContent = '▼';
    skillsSidebar.appendChild(scrollIndicator);

    const checkSkillsOverflow = () => {
        const hasOverflow = skillsSidebar.scrollHeight > skillsSidebar.clientHeight;
        skillsSidebar.classList.toggle('has-overflow', hasOverflow);

        // Check if scrolled to bottom (within 40px tolerance - hide as soon as last skill visible)
        const scrolledToBottom = skillsSidebar.scrollTop + skillsSidebar.clientHeight >= skillsSidebar.scrollHeight - 40;
        skillsSidebar.classList.toggle('scrolled-bottom', scrolledToBottom);
    };

    // Check on load and resize
    checkSkillsOverflow();
    window.addEventListener('resize', checkSkillsOverflow);
    skillsSidebar.addEventListener('scroll', checkSkillsOverflow);

    // Setup admin header button (dev mode only)
    const adminBtn = document.getElementById('admin-header-btn');
    if (IS_DEV && adminBtn) {
        adminBtn.style.display = 'flex';
        adminBtn.addEventListener('click', () => {
            router.navigate('#/admin');
        });
    }

    // Setup button handlers for all responsive variants
    // Helper to attach handler to all variants of a button
    const attachToAllVariants = (baseName, handler) => {
        const variants = [baseName, `${baseName}-full`, `${baseName}-med`];
        variants.forEach(id => {
            const el = document.getElementById(id);
            if (el) el.addEventListener('click', handler);
        });
    };

    // Save button with feedback
    const handleSave = (e) => {
        saveGame();
        const btn = e.target;
        const originalText = btn.textContent;
        btn.textContent = 'Saved!';
        btn.disabled = true;
        setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
        }, 1500);
    };
    attachToAllVariants('save-button', handleSave);

    attachToAllVariants('settings-button', () => openSettingsModal());
    attachToAllVariants('bank-button', () => openBankModal());
    attachToAllVariants('shop-button', () => openShopModal());
    attachToAllVariants('glossary-button', () => openGlossaryModal());
    attachToAllVariants('items-button', () => openItemDatabaseModal());

    // Header quest button opens the Quest modal
    document.getElementById('header-quests').addEventListener('click', () => {
        showQuestModal();
    });

    // Header tome opens the Tome modal
    document.getElementById('header-tome').addEventListener('click', () => {
        openTomeModal();
    });

    // Initialize hamburger menus (small and medium variants)
    initHamburgerMenus();

    // Initialize sidebar tab switching
    initSidebarTabs();

    // Initialize talent activity panel
    initTalentActivity();

    // Initialize inventory UI with drag-and-drop
    initInventoryUI();

    // Initialize equipment UI
    initEquipmentUI();

    // Initialize pinned quests UI
    initPinnedQuestsUI();

    // Update session time every second (also updates Tome count)
    setInterval(updateSessionTime, 1000);

    // Initial Tome and Quest button count updates
    updateTomeButtonCount();
    updateQuestButtonCount();

    // Setup discovery notification callback
    gameState.onNewDiscovery = (itemId, discoveryNumber) => {
        showDiscoveryNotification(itemId, discoveryNumber);
    };
}

/**
 * Initialize sidebar tab switching
 */
function initSidebarTabs() {
    const tabs = document.querySelectorAll('.sidebar-tab');
    const panels = document.querySelectorAll('.tab-panel');

    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const targetTab = tab.dataset.tab;

            // Update active tab button
            tabs.forEach(t => t.classList.remove('active'));
            tab.classList.add('active');

            // Update active panel
            panels.forEach(panel => {
                panel.classList.toggle('active', panel.id === `tab-${targetTab}`);
            });

            // Store preference
            localStorage.setItem('idleRealms_sidebarTab', targetTab);
        });
    });

    // Restore saved tab preference
    const savedTab = localStorage.getItem('idleRealms_sidebarTab');
    if (savedTab) {
        const tabButton = document.querySelector(`.sidebar-tab[data-tab="${savedTab}"]`);
        if (tabButton) {
            tabButton.click();
        }
    }
}

/**
 * Initialize hamburger menus (small and medium screen variants)
 */
function initHamburgerMenus() {
    // Menu configurations: [buttonId, menuId]
    const menuConfigs = [
        ['sidebar-menu-btn', 'sidebar-menu'],       // Small screens
        ['sidebar-menu-btn-med', 'sidebar-menu-med'] // Medium screens
    ];

    const allMenus = [];

    menuConfigs.forEach(([btnId, menuId]) => {
        const menuBtn = document.getElementById(btnId);
        const menu = document.getElementById(menuId);

        if (!menuBtn || !menu) return;

        allMenus.push({ btn: menuBtn, menu });

        // Toggle menu on button click
        menuBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            // Close other menus first
            allMenus.forEach(({ menu: otherMenu }) => {
                if (otherMenu !== menu) otherMenu.classList.add('hidden');
            });
            menu.classList.toggle('hidden');
        });

        // Close menu when clicking a menu item
        menu.querySelectorAll('button').forEach(btn => {
            btn.addEventListener('click', () => {
                menu.classList.add('hidden');
            });
        });
    });

    // Close all menus when clicking outside
    document.addEventListener('click', (e) => {
        allMenus.forEach(({ btn, menu }) => {
            if (!menu.contains(e.target) && !btn.contains(e.target)) {
                menu.classList.add('hidden');
            }
        });
    });
}

/**
 * Update session time display, Tome button count, and Quest button count
 */
function updateSessionTime() {
    const elapsed = Math.floor((Date.now() - gameState.time.sessionStart) / 1000);
    document.getElementById('game-time').textContent = `Session: ${formatTime(elapsed)}`;

    // Update Tome button count
    updateTomeButtonCount();

    // Update Quest button count and indicator
    updateQuestButtonCount();
}

/**
 * Update the quest button count and timer in the header
 */
function updateQuestButtonCount() {
    const questProgress = document.getElementById('header-quest-progress');
    if (questProgress) {
        questProgress.textContent = getQuestButtonText();
    }

    // Update active quest timer
    const questTimer = document.getElementById('header-quest-timer');
    if (questTimer) {
        const timerText = getActiveQuestTimerText();
        questTimer.textContent = timerText;
        questTimer.style.display = timerText ? 'inline' : 'none';

        // Add complete class when ready
        if (hasQuestReadyToComplete()) {
            questTimer.classList.add('complete');
        } else {
            questTimer.classList.remove('complete');
        }
    }

    // Add glow effect if quest is ready to complete
    const questButton = document.getElementById('header-quests');
    if (questButton) {
        if (hasQuestReadyToComplete()) {
            questButton.classList.add('quest-ready');
        } else {
            questButton.classList.remove('quest-ready');
        }
    }
}

/**
 * Save game to localStorage
 */
function saveGame() {
    // Skip saving if delete is in progress
    if (gameState._deletingSave) {
        logger.debug('[SAVE] Skipped - delete in progress');
        return;
    }

    try {
        // Copy current save to backup before overwriting — only if it's valid JSON
        const currentSave = localStorage.getItem('idleRealms_save');
        if (currentSave) {
            try {
                JSON.parse(currentSave);
                localStorage.setItem('idleRealms_save_backup', currentSave);
            } catch (e) {
                // Primary is already corrupted — don't overwrite a good backup with bad data
                logger.warn('[SAVE] Primary save corrupted, skipping backup copy');
            }
        }

        const saveData = gameState.export();
        localStorage.setItem('idleRealms_save', JSON.stringify(saveData));
        gameState.time.lastSave = Date.now();
        logger.save('Game saved');
    } catch (error) {
        logger.error('Failed to save game:', error);
    }
}

/**
 * Load game from localStorage
 */
function loadGame() {
    try {
        let saveData = localStorage.getItem('idleRealms_save');
        let usedBackup = false;

        // Try parsing primary save, fall back to backup if corrupted
        if (saveData) {
            try {
                JSON.parse(saveData);
            } catch (e) {
                logger.warn('[LOAD] Primary save corrupted, trying backup...');
                saveData = localStorage.getItem('idleRealms_save_backup');
                usedBackup = true;
                if (saveData) {
                    try {
                        JSON.parse(saveData);
                    } catch (e2) {
                        logger.error('[LOAD] Backup also corrupted');
                        saveData = null;
                    }
                }
            }
        }

        logger.debug('[OFFLINE] saveData exists:', !!saveData);

        if (saveData) {
            const parsed = JSON.parse(saveData);

            if (usedBackup) {
                setTimeout(() => {
                    toastWarning('Save was corrupted — restored from backup (up to 30s of progress may be lost)');
                }, 1000);
            }

            logger.debug('[OFFLINE] parsed.savedAt:', parsed.savedAt);
            logger.debug('[OFFLINE] parsed.player?.currentAction:', parsed.player?.currentAction);
            logger.debug('[OFFLINE] Time since save (seconds):', Math.floor((Date.now() - parsed.savedAt) / 1000));

            // Import the saved state FIRST so offline progress can access guild/pet/blueprint data
            // We pass the saved action separately since calculateOfflineProgress needs it
            gameState.import(parsed);

            // Clean up any zero-quantity items stuck in inventory (corrupted save edge case)
            const invSlots = gameState.getInventorySlots();
            for (let i = 0; i < invSlots.length; i++) {
                if (invSlots[i] !== null && invSlots[i].quantity <= 0) {
                    logger.warn(`[LOAD] Cleaned up zero-quantity item: ${invSlots[i].itemId} in slot ${i}`);
                    invSlots[i] = null;
                }
            }

            // Calculate offline progress AFTER importing
            // (guild progress, pet contracts, etc. need gameState to be loaded)
            const progress = calculateOfflineProgress(
                parsed.savedAt,
                parsed.player?.currentAction
            );

            logger.debug('[OFFLINE] calculateOfflineProgress result:', progress);
            if (progress) {
                logger.debug('[OFFLINE] offlineSeconds:', progress.offlineSeconds);
                logger.debug('[OFFLINE] hadAction:', progress.hadAction);
                logger.debug('[OFFLINE] actionsCompleted:', progress.actionsCompleted);
                logger.debug('[OFFLINE] Will show modal:', progress.offlineSeconds >= 60);
            }
            logger.save('Game loaded');

            // Reinitialize starting tools (in case save was from before tool system)
            reinitializeStartingTools();

            // Enforce Grand Architect talent: reroll any leftover uncommon contracts
            if (getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractRarityBoost') > 0) {
                architectingSkill.rerollUncommonContracts();
            }

            // Store action info for potential restart
            const savedAction = parsed.player?.currentAction;

            // Clear the restored action - it has no tick function after JSON deserialization
            if (gameState.getCurrentAction()) {
                gameState.clearAction();
                logger.debug('Cleared restored action (tick function not serializable)');
            }

            // Apply and show offline progress if applicable
            if (progress && progress.offlineSeconds >= 60) {
                const appliedProgress = applyOfflineProgress(progress);
                logger.debug('[OFFLINE] Applied progress, showing modal...');
                // Show modal after a short delay to let UI initialize
                setTimeout(() => {
                    showOfflineModal(appliedProgress);

                    // After showing modal, restart the action if we had one
                    if (savedAction && savedAction.type) {
                        restartSavedAction(savedAction);
                    }
                }, 500);
            } else if (savedAction && savedAction.type) {
                // If offline time was too short for modal, still restart the action
                logger.debug('[OFFLINE] No modal (< 60s), but restarting action...');
                restartSavedAction(savedAction);
            }
        } else {
            // No save data - new player, show welcome modal
            logger.debug('[WELCOME] No save data found, showing welcome modal');
            setTimeout(() => {
                showWelcomeModal();
            }, 500);
        }
    } catch (error) {
        logger.error('Failed to load game:', error);
    }
}

/**
 * Restart a saved action after reload
 * @param {object} savedAction - The action data from the save
 */
function restartSavedAction(savedAction) {
    logger.debug('[OFFLINE] Restarting action:', savedAction.type);

    // Dynamically import the skill module and restart the action
    switch (savedAction.type) {
        case 'mining':
            import('./skills/mining/mining.js').then(({ miningSkill }) => {
                if (savedAction.rockId) {
                    miningSkill.startMining(savedAction.rockId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted mining:', savedAction.rockId);
                }
            });
            break;
        case 'woodcutting':
            import('./skills/woodcutting/woodcutting.js').then(({ woodcuttingSkill }) => {
                if (savedAction.treeId) {
                    woodcuttingSkill.startChopping(savedAction.treeId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted woodcutting:', savedAction.treeId);
                }
            });
            break;
        case 'fishing':
            import('./skills/fishing/fishing.js').then(({ fishingSkill }) => {
                if (savedAction.fishId) {
                    fishingSkill.startFishing(savedAction.fishId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted fishing:', savedAction.fishId);
                }
            });
            break;
        case 'pilfering':
            import('./skills/pilfering/pilfering.js').then(({ pilferingSkill }) => {
                if (savedAction.targetId) {
                    pilferingSkill.startPickpocketing(savedAction.targetId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted pilfering:', savedAction.targetId);
                }
            });
            break;
        case 'farming':
            import('./skills/farming/farming.js').then(({ farmingSkill }) => {
                if (savedAction.subType === 'raking') {
                    farmingSkill.startRaking();
                    logger.debug('[OFFLINE] Restarted farming: raking');
                }
            });
            break;
        case 'crafting':
            import('./skills/crafting/crafting.js').then(({ craftingSkill }) => {
                if (savedAction.itemId) {
                    if (savedAction.subType === 'ranged' || savedAction.isArmor) {
                        craftingSkill.startCraftingArmor(savedAction.itemId.toLowerCase());
                        logger.debug('[OFFLINE] Restarted ranged armor crafting:', savedAction.itemId);
                    } else if (savedAction.subType === 'magic') {
                        craftingSkill.startCraftingMagicArmor(savedAction.itemId.toLowerCase());
                        logger.debug('[OFFLINE] Restarted magic armor crafting:', savedAction.itemId);
                    } else {
                        craftingSkill.startCraftingJewelry(savedAction.itemId.toLowerCase());
                        logger.debug('[OFFLINE] Restarted jewelry crafting:', savedAction.itemId);
                    }
                }
            });
            break;
        case 'smithing':
            import('./skills/smithing/smithing.js').then(({ smithingSkill }) => {
                if (savedAction.arrowheadId) {
                    smithingSkill.startSmithingArrowheads(savedAction.arrowheadId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing arrowheads:', savedAction.arrowheadId);
                } else if (savedAction.swordId) {
                    smithingSkill.startSmithingSword(savedAction.swordId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing sword:', savedAction.swordId);
                } else if (savedAction.dirkId) {
                    smithingSkill.startSmithingDirk(savedAction.dirkId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing dirk:', savedAction.dirkId);
                } else if (savedAction.greatswordId) {
                    smithingSkill.startSmithingGreatsword(savedAction.greatswordId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing greatsword:', savedAction.greatswordId);
                } else if (savedAction.meleeArmorId) {
                    smithingSkill.startSmithingMeleeArmor(savedAction.meleeArmorId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing armor:', savedAction.meleeArmorId);
                } else if (savedAction.bulwarkId) {
                    smithingSkill.startSmithingBulwark(savedAction.bulwarkId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing bulwark:', savedAction.bulwarkId);
                } else if (savedAction.barId) {
                    smithingSkill.startSmithing(savedAction.barId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted smithing bar:', savedAction.barId);
                }
            });
            break;
        case 'pyre':
            import('./skills/pyre/pyre.js').then(({ pyreSkill }) => {
                if (savedAction.logId) {
                    pyreSkill.startBurning(savedAction.logId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted pyre:', savedAction.logId);
                }
            });
            break;
        case 'pyre_ritual':
            import('./skills/pyre/pyre.js').then(({ pyreSkill }) => {
                if (savedAction.logId && savedAction.ritualId) {
                    pyreSkill.startRitual(savedAction.logId.toLowerCase(), savedAction.ritualId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted pyre ritual:', savedAction.logId, savedAction.ritualId);
                }
            });
            break;
        case 'cooking':
            import('./skills/cooking/cooking.js').then(({ cookingSkill }) => {
                if (savedAction.foodId) {
                    cookingSkill.startCooking(savedAction.foodId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted cooking:', savedAction.foodId);
                }
            });
            break;
        case 'empowering':
            import('./skills/cooking/cooking.js').then(({ cookingSkill }) => {
                if (savedAction.recipeId) {
                    cookingSkill.startEmpowering(savedAction.recipeId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted empowering:', savedAction.recipeId);
                }
            });
            break;
        case 'woodworking':
            import('./skills/woodworking/woodworking.js').then(({ woodworkingSkill }) => {
                if (savedAction.recipeId) {
                    // Determine which type of woodworking action it was
                    if (savedAction.subType === 'shafts') {
                        woodworkingSkill.startMakingShafts(savedAction.recipeId.toLowerCase());
                    } else if (savedAction.subType === 'arrows') {
                        woodworkingSkill.startMakingArrows(savedAction.recipeId.toLowerCase());
                    } else if (savedAction.subType === 'bows') {
                        woodworkingSkill.startMakingBow(savedAction.recipeId.toLowerCase());
                    } else if (savedAction.subType === 'staffs') {
                        woodworkingSkill.startMakingStaff(savedAction.recipeId.toLowerCase());
                    }
                    logger.debug('[OFFLINE] Restarted woodworking:', savedAction.recipeId);
                }
            });
            break;
        case 'glyphbinding':
            import('./skills/glyphbinding/glyphbinding.js').then(({ glyphbindingSkill }) => {
                if (savedAction.subType === 'ichor' && savedAction.node?.id) {
                    glyphbindingSkill.startGatheringIchor(savedAction.node.id.toLowerCase());
                    logger.debug('[OFFLINE] Restarted glyphbinding ichor:', savedAction.node.id);
                } else if (savedAction.subType === 'glyph' && savedAction.glyph?.id) {
                    glyphbindingSkill.startCraftingGlyph(savedAction.glyph.id.toLowerCase());
                    logger.debug('[OFFLINE] Restarted glyphbinding glyph:', savedAction.glyph.id);
                } else if (savedAction.subType === 'glyphCore' && savedAction.glyphCore?.id) {
                    glyphbindingSkill.startCraftingGlyphCore(savedAction.glyphCore.id.toLowerCase());
                    logger.debug('[OFFLINE] Restarted glyphbinding glyphCore:', savedAction.glyphCore.id);
                } else if (savedAction.subType === 'focus' && savedAction.focus?.id) {
                    glyphbindingSkill.startCraftingFocus(savedAction.focus.id.toLowerCase());
                    logger.debug('[OFFLINE] Restarted glyphbinding focus:', savedAction.focus.id);
                }
            });
            break;
        case 'alchemy':
            import('./skills/alchemy/alchemy.js').then(({ alchemySkill }) => {
                if (savedAction.subType === 'cleaning' && savedAction.recipe?.id) {
                    alchemySkill.startCleaningHerb(savedAction.recipe.id.toLowerCase());
                    logger.debug('[OFFLINE] Restarted alchemy cleaning:', savedAction.recipe.id);
                } else if (savedAction.subType === 'potion' && savedAction.recipe?.id) {
                    alchemySkill.startBrewingPotion(savedAction.recipe.id.toLowerCase());
                    logger.debug('[OFFLINE] Restarted alchemy brewing:', savedAction.recipe.id);
                }
            });
            break;
        case 'gatherer':
            import('./skills/gatherer/gatherer.js').then(({ gathererSkill }) => {
                if (savedAction.zoneId) {
                    gathererSkill.startGathering(savedAction.zoneId.toLowerCase());
                    logger.debug('[OFFLINE] Restarted gatherer:', savedAction.zoneId);
                }
            });
            break;
        case 'combat':
            import('./skills/combat/combat.js').then(({ combatSkill }) => {
                if (savedAction.zoneId) {
                    const tier = savedAction.savedStats?.tier || null;
                    combatSkill.startFighting(savedAction.zoneId.toLowerCase(), tier);
                    logger.debug('[OFFLINE] Restarted combat:', savedAction.zoneId, tier ? `(tier ${tier})` : '');
                }
            });
            break;
        case 'architecting':
            import('./skills/architecting/architecting.js').then(({ architectingSkill }) => {
                if (savedAction.blueprintId && savedAction.buildMode === 'active') {
                    architectingSkill.startQuickBuild(savedAction.blueprintId);
                    logger.debug('[OFFLINE] Restarted architecting quick build:', savedAction.blueprintId);
                }
            });
            break;
        default:
            logger.debug('[OFFLINE] Unknown action type:', savedAction.type);
    }
}

// Start the game when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGame);
} else {
    initGame();
}
